import React, { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Box, Checkbox, List } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { uniqBy as _uniqBy, get as _get } from 'lodash';
// import InfiniteScroll from 'react-infinite-scroll-component';
import VisibilitySensor from "react-visibility-sensor";

import { TreeSelectorNode } from "./TreeSelectorNode";
import { useTranslation } from "react-i18next";

const TreeSelectorNodeGroupComponent = ({
  nodes,
  value,
  invariants,
  parent = null,
  className = '',
  parentCheckboxWrapperClassName = '',
  level = 0,
  disablePadding = false,
  showLoading = true,
  showLoadingOnRoot = false,
  loadingText = '',
  showNoData = true,
  showNoDataOnRoot = false,
  pagination = null,
  noDataText = '',
  watchForChange = [],
  disableStoresIcon,
  checkDisabled = (node: any) => false,
  checkVisibility = (node: any) => true,
  onNodeExpanded = (node: any) => { },
  onChange = (node: any, value: any[]) => { },
  onNextNodesPage = (node: any) => { },
}: any, ref: any) => {
  const innerGroupConfig = {
    showLoading,
    loadingText,
    showNoData,
    noDataText,
    checkVisibility,
    checkDisabled,
    disableStoresIcon
  };
  const { steps: paginationSteps = 5, total: paginationTotalCount }: any = pagination || {};

  const { t } = useTranslation();

  noDataText = t('app.common.noItemsFound');
  loadingText = t('app.common.loading');

  const theme = useTheme();
  const refNodes = useRef<any>({});

  const [currentShownPage, setCurrentShownPage] = useState(1);
  const [forceLoading, setForceLoading] = useState<boolean>(false);
  const [checkedParent, setCheckedParent] = useState<boolean>(false);

  const hasNodes = useMemo(() => (nodes || []).length > 0, [nodes]);
  const isLoadingNodes = useMemo(() => !nodes, [nodes]);
  const hasNoChildren = useMemo(() => nodes?.find(node => node.hasChildren), [nodes]);
  const hasEmptyParent = hasNodes && parent === null && !hasNoChildren;

  const listStyle = useMemo(() => ({
    paddingLeft: theme.spacing(level),
  }), [level]);

  const computedTotalItems = useMemo(() => paginationTotalCount || nodes?.length, [paginationTotalCount, nodes?.length]);
  const computedShownNodesCount = useMemo(() => {
    let count = currentShownPage * paginationSteps;
    if (count > nodes?.length) {
      count = nodes?.length;
    }
    return count;
  }, [paginationSteps, currentShownPage, nodes?.length]);

  const shownNodes = useMemo(() => {
    let result = nodes?.slice(0, computedShownNodesCount);
    return result;
  }, [nodes, computedShownNodesCount]);

  const computedLoadingText = useMemo(() => {
    const evalText = typeof loadingText === 'function' ? loadingText : (arg: any) => loadingText;
    return evalText(parent);
  }, [loadingText, parent]);

  const computedNoDataText = useMemo(() => {
    const evalText = typeof noDataText === 'function' ? noDataText : (arg: any) => noDataText;
    return evalText(parent);
  }, [noDataText, parent]);

  const hasMoreNodes = useMemo(() => !isLoadingNodes && computedTotalItems > computedShownNodesCount, [computedTotalItems, computedShownNodesCount]);
  const computedShowNoData = useMemo(() => (showNoDataOnRoot || level > 0) && showNoData && !hasNodes && !isLoadingNodes, [showNoData, isLoadingNodes, hasNodes, level]);
  const computedShowLoading = useMemo(() => {
    return (showLoadingOnRoot || level > 0) && ((showLoading && isLoadingNodes) || hasMoreNodes);
  }, [showLoading, isLoadingNodes, level, hasMoreNodes]);
  const computedValueObj = useMemo(() => value?.reduce((acc, val) => (acc[val.path] = true, acc), {}), [value]);

  const toggleParentDependingValues = () => {
    const valuesObj = computedValueObj;
    // If a node is disabled, don't consider for allChecked criteria
    const areAllChecked = nodes?.every(node => valuesObj[node.path] || (typeof (checkDisabled) === "function" && checkDisabled(node)));
    if (!!areAllChecked !== checkedParent) {
      setCheckedParent(!!areAllChecked);
    }
  };

  function handleAllDisabledNodes(): boolean {
    const areAllDisabled = nodes?.every(node => checkDisabled(node));
    return areAllDisabled || false;
  }
  useEffect(() => {
    setForceLoading(true);
    const cancelToken = setTimeout(() => {
      setForceLoading(false);
    }, 110);
    return () => {
      clearTimeout(cancelToken);
    };
  }, [currentShownPage]);

  useEffect(() => {
    if (!hasEmptyParent) {
      return;
    }

    toggleParentDependingValues();
  }, [value]);

  useEffect(() => {
    toggleParentDependingValues();
  }, watchForChange);
  

  const handleChange = (node: any, selection: any[], currentlyActive, isChecked) => {
    const baseSelection = value.filter(item => !item.path.startsWith(node.path));
    const filteredSelection = [...baseSelection, ...selection].filter((item, idx, arr) => {
      return arr.findIndex(i => i.path === item.path) === idx;
    });
    onChange(node, filteredSelection, currentlyActive, isChecked);
  };
  const handleNext = async (isVisble) => {
    if (isVisble && nodes) {
      if (computedShownNodesCount > nodes.length - paginationSteps && computedShownNodesCount < computedTotalItems) {
        await Promise.resolve(onNextNodesPage(parent));
      }
      setCurrentShownPage(currentShownPage + 1);
    }
  };

  const handleToggleChildren = () => {
    setCheckedParent(prevCheckedParent => {
      const valuesObj = value?.reduce((acc, node) => (acc[node.path] = true, acc), {});
      const filteredNodes = nodes.filter(node => !valuesObj[node.path]);
      let values = [...value, ...filteredNodes];

      if (prevCheckedParent) {
        const nodesObj = nodes?.reduce((acc, node) => (acc[node.path] = true, acc), {});
        values = value.filter(val => !nodesObj[val.path]);
      }

      onChange(nodes, values);
      return !prevCheckedParent;
    });
  };

  useImperativeHandle(ref, () => ({
    collapseAll: () => Object.entries(refNodes.current).forEach(([key, ref]: any) => ref.collapseAll()),
    expandAll: () => Object.entries(refNodes.current).forEach(([key, ref]: any) => ref.expandAll()),
  }));

  function LoadingNode() {
    return (
      computedShowLoading ?
        <TreeSelectorNode
          key="node-loading"
          node={{ text: computedLoadingText, hasChildren: false }}
          hasCheck={false}
          textStyle={{ color: 'gray' }}
        />
        :
        <></>
    );
  };

  return <>
    <List component="nav" aria-labelledby="nested-list" className={className} style={listStyle} disablePadding={disablePadding}>
      {hasEmptyParent &&
        <Box className={parentCheckboxWrapperClassName}>
          <Checkbox
            checked={checkedParent}
            disabled={handleAllDisabledNodes()}
            onChange={handleToggleChildren}
          />
        </Box>
      }
      {hasNodes && shownNodes.map((node) => (
        <TreeSelectorNode
          {...innerGroupConfig}
          ref={(el) => refNodes.current = {
            ...refNodes.current,
            ...(!!el && {
              [`node_${node.path}`]: el,
            }),
          }}
          key={`node_${node.path}`}
          value={value}
          invariants={invariants}
          node={node}
          level={level}
          checkDisabled={checkDisabled}
          onExpanded={onNodeExpanded}
          onChange={(value, currentlyActive, isChecked) => handleChange(node, value, currentlyActive, isChecked)}
          onNextNodesPage={onNextNodesPage}
        />
      ))}
      {hasMoreNodes &&
        <VisibilitySensor active={!forceLoading} onChange={handleNext}>
          <LoadingNode />
        </VisibilitySensor>
      }
      {isLoadingNodes &&
        <LoadingNode />
      }
      {computedShowNoData &&
        <TreeSelectorNode
          key="node-loading"
          node={{ text: computedNoDataText, hasChildren: false }}
          hasCheck={false}
          textStyle={{ fontStyle: 'italic', color: 'gray' }}
          disableStoresIcon={disableStoresIcon}
        />
      }
    </List>
  </>;
};

export const TreeSelectorNodeGroup = memo(forwardRef(TreeSelectorNodeGroupComponent));
